import { useState } from 'react';
import { Modal } from '../ui/modal.jsx';

const SECTIONS = [
  { id: 'cert', label: 'Certificate', icon: 'fa-shield', filenameSuffix: '.crt' },
  { id: 'key',  label: 'Private key', icon: 'fa-key',     filenameSuffix: '.key', warn: true },
  { id: 'ca',   label: 'CA chain',    icon: 'fa-link',    filenameSuffix: '-ca.crt' },
];

function downloadText(filename, text) {
  const blob = new Blob([text], { type: 'application/x-pem-file' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function safeFilename(s) {
  return String(s || 'cert').replace(/[^A-Za-z0-9._-]+/g, '_').slice(0, 64);
}

export function IssuedBundleModal({ open, onClose, certificate, bundle }) {
  const [copied, setCopied] = useState(null);
  if (!open || !bundle) return null;

  const baseName = safeFilename(certificate?.subjectCn || 'node');

  function bodyFor(id) {
    if (id === 'cert') return bundle.certPem;
    if (id === 'key')  return bundle.keyPem;
    if (id === 'ca')   return bundle.caCertPem;
    return '';
  }

  async function copy(id) {
    const text = bodyFor(id);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      /* ignore */
    }
  }

  function downloadAll() {
    downloadText(`${baseName}.crt`, bundle.certPem);
    downloadText(`${baseName}.key`, bundle.keyPem);
    downloadText(`${baseName}-ca.crt`, bundle.caCertPem);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Certificate issued"
      description="Save this bundle now — the private key will not be shown again."
      size="xl"
      footer={
        <>
          <button type="button" onClick={downloadAll} className="btn-secondary" data-skip-autofocus>
            <i className="fa-solid fa-download" aria-hidden="true" /> Download all (3 files)
          </button>
          <button type="button" onClick={onClose} className="btn-primary">
            <i className="fa-solid fa-check" aria-hidden="true" /> I've saved it
          </button>
        </>
      }
    >
      <div className="rounded-2xl border border-warning/30 bg-warning-soft px-4 py-3 text-xs text-warning">
        <i className="fa-solid fa-triangle-exclamation mr-2" aria-hidden="true" />
        The private key is included in this bundle and is the only copy — TMS does not retain it.
      </div>

      <div className="mt-4 space-y-3">
        {SECTIONS.map((s) => {
          const text = bodyFor(s.id);
          return (
            <div key={s.id} className="rounded-2xl border border-line bg-canvas">
              <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
                <div className="flex items-center gap-2 text-sm font-semibold text-ink">
                  <i className={`fa-solid ${s.icon} ${s.warn ? 'text-warning' : 'text-accent'}`} aria-hidden="true" />
                  {s.label}
                  {s.warn && (
                    <span className="rounded-full border border-warning/30 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-warning">
                      Sensitive
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => copy(s.id)}
                    className="btn-ghost text-xs"
                  >
                    <i className={`fa-solid ${copied === s.id ? 'fa-check' : 'fa-copy'}`} aria-hidden="true" />
                    {copied === s.id ? 'Copied' : 'Copy'}
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadText(`${baseName}${s.filenameSuffix}`, text)}
                    className="btn-ghost text-xs"
                  >
                    <i className="fa-solid fa-download" aria-hidden="true" /> Download
                  </button>
                </div>
              </div>
              <pre className="overflow-x-auto px-4 py-3 text-[11px] leading-snug text-ink scrollbar-thin">
                {text}
              </pre>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
